/**
 * G19/DASH-2977 (FE-3): mirrors the BE `CensoredDataMethod` enum
 * (Web.Api.Core/Domain/Enums/CensoredDataMethod.cs). The integer values are a
 * persisted contract and MUST match the BE exactly — never re-number them.
 * Controls how non-detect (< LOQ) lab results are handled when computing Risk
 * Assessment exposure statistics.
 */
export enum CensoredDataMethod {
    /** Substitute each non-detect with LOD / √2 (default; current behaviour). */
    LodOverSqrt2 = 0,
    /** Substitute each non-detect with the LOD as reported (the full limit of detection). */
    LodAsReported = 1,
    /** Substitute each non-detect with LOD / 2. */
    LodOverTwo = 2,
    /** Use the reported value as-is (no substitution applied to non-detects). */
    UseAsReported = 3,
    /** Exclude non-detects entirely from the statistics (changes the sample count N). */
    ExcludeNonDetects = 4,
}

/**
 * Ordered options for the non-detect handling `mat-select` on the jurisdiction
 * risk-config form. Labels are English source strings resolved through the
 * translate pipe in the template.
 */
export const CENSORED_DATA_METHOD_OPTIONS: { value: CensoredDataMethod; label: string }[] = [
    { value: CensoredDataMethod.LodOverSqrt2, label: 'LOD / √2 (default)' },
    { value: CensoredDataMethod.LodAsReported, label: 'LOD as reported' },
    { value: CensoredDataMethod.LodOverTwo, label: 'LOD / 2' },
    { value: CensoredDataMethod.UseAsReported, label: 'Use as reported' },
    { value: CensoredDataMethod.ExcludeNonDetects, label: 'Exclude non-detects' },
];

/**
 * Read-only display labels for the *snapshotted* method actually used to compute a Risk
 * Register's statistics (RA meta strip, AC8). Distinct from `CENSORED_DATA_METHOD_OPTIONS`:
 * the "(default)" suffix there is form-selection copy and must NOT leak into a "what was
 * actually used" strip, where every register would otherwise read "LOD / √2 (default)"
 * because the BE field is non-nullable and defaults to 0.
 */
export const CENSORED_DATA_METHOD_DISPLAY_LABELS: Record<CensoredDataMethod, string> = {
    [CensoredDataMethod.LodOverSqrt2]: 'LOD / √2',
    [CensoredDataMethod.LodAsReported]: 'LOD as reported',
    [CensoredDataMethod.LodOverTwo]: 'LOD / 2',
    [CensoredDataMethod.UseAsReported]: 'Use as reported',
    [CensoredDataMethod.ExcludeNonDetects]: 'Exclude non-detects',
};
